import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Button, FormLayout, Heading, Input, Select, Switch } from "@/ui";
import { useForm, useValidator } from "@/hooks";
import styles from "./StrongholdForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const StrongholdCredentialForm = ({
    isCreate,
    handleCancel,
    app,
    selectCredential,
    setCredentials,
    isUpdating,
    handlerUpdatingOrCreate,
    stores,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "stronghold_store_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Store provider id is required.",
            },
            {
                field: "stronghold_sh_secret_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Secret Key is required.",
            },
            {
                field: "stronghold_sh_integration_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Integration key is required.",
            },
            {
                field: "stronghold_api_host",
                method: "isEmpty",
                validWhen: false,
                message: "This Api Host is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const storeOptions = useMemo(
        () =>
            (stores || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [stores],
    );

    useEffect(() => {
        if (!isCreate) {
            const objSelect = app?.credentials[selectCredential];
            setInputs({
                stronghold_sh_secret_key: objSelect?.stronghold_sh_secret_key,
                stronghold_sh_integration_key:
                    objSelect?.stronghold_sh_integration_key,
                stronghold_api_host: objSelect?.stronghold_api_host,
                stronghold_store_provider_id:
                    objSelect?.stronghold_store_provider_id,
                stronghold_allow_tiping: objSelect?.stronghold_allow_tiping,
            });
        }
    }, []);

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            let accounts = _.cloneDeep(app.credentials ?? []);

            if (isCreate) {
                accounts.push({
                    stronghold_sh_secret_key: inputs?.stronghold_sh_secret_key,
                    stronghold_sh_integration_key:
                        inputs?.stronghold_sh_integration_key,
                    stronghold_api_host: inputs?.stronghold_api_host,
                    stronghold_store_provider_id:
                        inputs?.stronghold_store_provider_id,
                    stronghold_allow_tiping: inputs?.stronghold_allow_tiping,
                });
            } else {
                accounts[selectCredential] = inputs;
            }
            setCredentials(accounts);
            handlerUpdatingOrCreate(accounts);
        }
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleStoreProviderChange = value => {
        const store = (stores || []).find(s => String(s.value) === value);
        setInputs({
            ...inputs,
            stronghold_store_provider_id: store ? store.value : value,
        });
    };

    return (
        <div className={styles.credentialForm}>
            <Heading level={5}>
                {isCreate ? "Add Credential" : "Update Credential"}
            </Heading>

            <FormLayout>
                <FormLayout.Section title="Credential Informations">
                    <Switch
                        checked={Boolean(inputs.stronghold_allow_tiping)}
                        label="Allow Tipping"
                        name="stronghold_allow_tiping"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Select
                            error={fieldError(
                                validator,
                                "stronghold_store_provider_id",
                            )}
                            fullWidth
                            label="Store Provider Id"
                            onValueChange={handleStoreProviderChange}
                            options={storeOptions}
                            value={
                                inputs.stronghold_store_provider_id != null
                                    ? String(
                                          inputs.stronghold_store_provider_id,
                                      )
                                    : undefined
                            }
                        />
                    </FormLayout.Grid>
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            error={fieldError(validator, "stronghold_api_host")}
                            fullWidth
                            label="Api Host"
                            name="stronghold_api_host"
                            onChange={inputChange}
                            placeholder="Api Host"
                            value={inputs.stronghold_api_host || ""}
                        />
                    </FormLayout.Grid>
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            error={fieldError(
                                validator,
                                "stronghold_sh_secret_key",
                            )}
                            fullWidth
                            label="SH Secret Key"
                            name="stronghold_sh_secret_key"
                            onChange={inputChange}
                            placeholder="SH Secret Key"
                            value={inputs.stronghold_sh_secret_key || ""}
                        />
                        <Input
                            error={fieldError(
                                validator,
                                "stronghold_sh_integration_key",
                            )}
                            fullWidth
                            label="SH Integration Key"
                            name="stronghold_sh_integration_key"
                            onChange={inputChange}
                            placeholder="SH Integration Key"
                            value={inputs.stronghold_sh_integration_key || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>
            </FormLayout>

            <div className={styles.credentialActions}>
                <Button
                    disabled={isUpdating}
                    onClick={handleClose}
                    type="button"
                    variant="secondary"
                >
                    Cancel
                </Button>
                <Button
                    loading={isUpdating}
                    onClick={createOrUpdate}
                    type="button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default StrongholdCredentialForm;
